import { useAtomValue } from "jotai";
import { useEffect, useRef } from "react";

import { useFeatureFlagGate } from "~/shared/hooks/use-feature-flag-gate.ts";
import { ldIdentifiedContextKeyAtom } from "~/shared/state/feature-flags-state.ts";
import { selectedCustomerAtom } from "~/shared/state/user-profile-state.ts";
import { consumeLoginEventPending } from "~/shared/utils/login-event-session.ts";

import { useSaveUserLoginEvents } from "./use-save-user-login-events.ts";

/**
 * Records a login event once per authenticated B2C session, after the customer
 * is resolved so the request carries the customer identity header. Mirrors the
 * legacy Trax UI, which saved login events once a logged-in customer id was
 * available.
 *
 * Gated behind the `logUserLoginEvents` LaunchDarkly flag so the call can be
 * disabled without a redeploy.
 *
 * Emission is tied to the login-events session marker, not this component's
 * lifetime: a genuine login (see `markLoginEventPending` in `b2c-utils.ts`) and
 * a deliberate profile switch (see `profile-switcher.tsx`) each set a pending
 * marker, which we consume exactly once per page load. A passive browser
 * refresh does not set the marker, so it re-mounts this tracker without
 * recording a spurious login.
 */
export function UserLoginEventsTracker() {
	const selectedCustomer = useAtomValue(selectedCustomerAtom);
	const { isReady, isEnabled } = useFeatureFlagGate("logUserLoginEvents");
	const identifiedContextKey = useAtomValue(ldIdentifiedContextKeyAtom);
	const { mutate: saveUserLoginEvents } = useSaveUserLoginEvents();
	const hasHandledRef = useRef(false);

	useEffect(() => {
		if (hasHandledRef.current) return;
		// Wait for the flag to resolve before touching the pending marker.
		if (!isReady) return;
		// A customer id of 0 means no accessible customer; skip it, matching the
		// legacy truthy-customerId guard.
		if (!selectedCustomer?.id) return;
		// The flag is customer-targeted and `FeatureFlagsProvider` identifies the
		// customer context asynchronously after the customer renders. Wait until
		// that identify has resolved for THIS customer so `isEnabled` reflects the
		// customer's flag value, not the previous anonymous one. This is a one-shot
		// decision (latched below), so acting on the stale anonymous value would be
		// uncorrectable. If identify fails the provider leaves this key unset, so
		// this fails closed: no event is recorded (see the provider's catch).
		if (identifiedContextKey !== String(selectedCustomer.id)) return;

		hasHandledRef.current = true;
		// Flag definitively off for the resolved customer: discard any pending
		// marker so a later flag-on reload can't backfill a disabled-session login.
		if (!isEnabled) {
			consumeLoginEventPending();
			return;
		}
		if (consumeLoginEventPending()) {
			saveUserLoginEvents();
		}
	}, [
		selectedCustomer,
		isReady,
		isEnabled,
		identifiedContextKey,
		saveUserLoginEvents,
	]);

	return null;
}
